import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Input,
  Badge,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
} from "reactstrap";
import toast from "react-hot-toast";
import { Triangle } from "react-loader-spinner";

import EventService from "../../services/KangarooServices/EventService";
import DeleteModal from "../../Components/Common/DeleteModal";
import CustomDataTable from "../../Components/CustomTable/CustomTable";
import BreadCrumb from "../../Components/Common/BreadCrumb";

const EventFormList = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterEmail, setFilterEmail] = useState("");
  const [filterPhone, setFilterPhone] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [adminNotesError, setAdminNotesError] = useState("");

  const statusOptions = [
    { label: "All", value: "" },
    { label: "New", value: 4 },
    { label: "InReview", value: 2 },
    { label: "Rejected", value: 3 },
    { label: "Accepted", value: 1 },
  ];

  const toggleStatus = () => setIsStatusOpen(!isStatusOpen);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Call server API with filters
      const result = await EventService.getFormList({
        email: filterEmail,
        phoneNumber: filterPhone,
        status: filterStatus,
      });

      setData(result);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };
  const handleStatusChange = (e) => {
    setSelectedStatus(Number(e.target.value));
  };

  // Fetch data on mount & whenever filters change
  useEffect(() => {
    fetchData();
  }, [filterEmail, filterPhone, filterStatus]);

  const handleStatusUpdate = (item) => {
    setSelectedItem(item);
    const currentStatus = statusOptions.find(
      (status) => status.label === item.formStatus,
    );
    setSelectedStatus(currentStatus ? currentStatus.value : null);
    toggleStatus();
  };

  const handleUpdateStatus = async () => {
    if (!adminNotes) {
      setAdminNotesError("Admin Notes is required");
      return;
    }

    if (selectedItem && selectedStatus !== null) {
      try {
        await EventService.updateStatus({
          id: selectedItem.id,
          formStatus: selectedStatus,
          adminNotes,
        });
        toast.success("Status updated successfully");
        fetchData();
      } catch (error) {
        toast.error(
          error?.response?.data?.message || "Failed to update status",
        );
      } finally {
        toggleStatus();
        setSelectedItem(null);
        setSelectedStatus(null);
        setAdminNotes("");
        setAdminNotesError("");
      }
    }
  };

  const columns = [
    {
      name: "S. N",
      cell: (row, index) => <div>{index + 1}</div>,
      width: "90px",
      center: true,
    },
    {
      name: "Name",
      selector: (row) => row.fullName,
      sortable: true,
    },
    {
      name: "Email",
      selector: (row) => row.email,
      sortable: true,
    },
    {
      name: "Phone",
      selector: (row) => row.phone || "N/A",
      sortable: true,
      cell: (row) => (
        <div>
          {row.phoneNumberCode} {row.phone || "N/A"}
        </div>
      ),
    },
    {
      name: "Status",
      selector: (row) => row.formStatus,
      sortable: true,
      cell: (row) => {
        let color;
        switch (row.formStatus) {
          case "Accepted":
            color = "success";
            break;
          case "Rejected":
            color = "danger";
            break;
          case "InReview":
            color = "warning";
            break;
          default:
            color = "primary";
        }
        return <Badge color={color}>{row.formStatus}</Badge>;
      },
    },
    {
      name: "Actions",
      center: true,
      cell: (row) => (
        <Button
          size="sm"
          color="warning"
          onClick={() => handleStatusUpdate(row)}
        >
          <i className="ri-pencil-line"></i>
        </Button>
      ),
    },
  ];

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Event Forms" pageTitle="User Management" />
        <Row className="mb-4">
          <Col lg={3}>
            <Input
              type="text"
              placeholder="Search by email..."
              value={filterEmail}
              onChange={(e) => setFilterEmail(e.target.value)}
            />
          </Col>

          <Col lg={3}>
            <Input
              type="text"
              placeholder="Search by phone..."
              value={filterPhone}
              onChange={(e) => setFilterPhone(e.target.value)}
            />
          </Col>

          <Col lg={3}>
            <select
              className="form-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              {statusOptions.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </Col>
        </Row>

        <Card>
          <CardBody>
            <CustomDataTable
              columns={columns}
              data={data}
              pagination
              striped
              progressPending={loading}
            />
          </CardBody>
        </Card>

        <Modal isOpen={isStatusOpen} toggle={toggleStatus}>
          <ModalHeader toggle={toggleStatus}>Update Status</ModalHeader>
          <ModalBody>
            <div className="mb-3">
              <label htmlFor="statusSelect" className="form-label">
                Select Status
              </label>
              <select
                id="statusSelect"
                className="form-select"
                value={
                  selectedStatus || selectedItem?.jobApplicationStatus || ""
                }
                onChange={handleStatusChange}
              >
                <option value="">-- Select Status --</option>
                {statusOptions.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>
            <FormGroup>
              <Label for="adminNotes">
                Admin Notes<span className="text-danger">*</span>
              </Label>
              <Input
                id="adminNotes"
                name="adminNotes"
                type="textarea"
                rows={6}
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                onBlur={() => {
                  if (!adminNotes)
                    setAdminNotesError("Admin Notes is required");
                  else setAdminNotesError("");
                }}
                invalid={!!adminNotesError}
              />
              {adminNotesError && (
                <div className="text-danger mt-1">{adminNotesError}</div>
              )}
            </FormGroup>
          </ModalBody>

          <ModalFooter>
            <button className="btn btn-success" onClick={handleUpdateStatus}>
              Update
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={toggleStatus}
            >
              Cancel
            </button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default EventFormList;
